import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApiService } from '../../core/services/api.service';
import Swal from 'sweetalert2';
import { RoleService } from "../../core/services/role.service";
import { SocketService } from '../../core/services/socket.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-incident-list',
  templateUrl: './incident-list.component.html',
  styleUrls: ['./incident-list.component.scss'],
  standalone: false
})
export class IncidentListComponent implements OnInit, OnDestroy {
  Math = Math; // For template usage
  incidents: any[] = [];
  displayIncidents:any[] =[];
  loading = false;
  error: string | null = null;
  
  // Filter options
  incidentStatus: string[] = ['reported','dispatched','acknowledged','completed','cancelled','in_progress','pending'];
  fireStations: any[] = [];
  zones: any[] = [];
  wards: any[] = [];
  severities: any[] = [];
  incidentTypes: any[] = [];
  
  // Selected filters
  selectedStatus: string | null = null;
  selectedDate: string | null = null;
  selectedEndDate: string | null = null;
  selectedFireStationId: number | null = null;
  selectedWardId: number | null = null;
  selectedZoneId: number | null = null;
  selectedSeverityId: number | null = null;
  selectedIncidentTypeId: number | null = null;
  
  // Pagination
  currentPage: number = 1;
  pageSize: number = 20;
  totalItems: number = 0;
  totalPages: number = 0;
  
  loadingFilters = false;
  private socketSubscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService, 
    public roleService: RoleService,
    private socketService: SocketService
  ) {}

  ngOnInit(): void {
    this.loadFilterOptions();
    
    // Set today's date as default for both start and end date
    const today = new Date().toISOString().split('T')[0];
    this.selectedDate = '2026-08-01'; 
    this.selectedEndDate = today;
    this.selectedStatus = 'all';
    
    this.loadIncidents();
    
    // Listen for socket events to refresh the list
    // Global notifications are handled in AppComponent
    const incidentCreatedSub = this.socketService.onIncidentCreated$.subscribe((data) => {
      if (data && data.incident) {
        // Refresh the entire list to respect current filters
        this.loadIncidents();
      }
    });
    
    const vehicleAssignedSub = this.socketService.onVehicleAssigned$.subscribe((data) => {
      if (data && data.incident) {
        // Refresh list as status might have changed
        this.loadIncidents();
      }
    });
    
    const crewAssignedSub = this.socketService.onCrewAssigned$.subscribe((data) => {
      if (data && data.incident) {
        // Refresh list as status might have changed
        this.loadIncidents();
      }
    });
    
    const incidentUpdatedSub = this.socketService.onIncidentUpdated$.subscribe((data) => {
      if (data && data.incident) {
        // Refresh list as incident details or status might have changed
        this.loadIncidents();
      }
    });
    
    this.socketSubscriptions.push(incidentCreatedSub, vehicleAssignedSub, crewAssignedSub, incidentUpdatedSub);
  }

  loadFilterOptions(): void {
    this.loadingFilters = true;
    
    this.apiService.get('/fire-stations').subscribe({
      next: (response: any) => {
        this.fireStations = response.data || [];
      },
      error: (error) => console.error('Error loading fire stations:', error)
    });
    
    // this.apiService.get('/zone').subscribe({
    //   next: (response: any) => {
    //     this.zones = response.data || [];
    //   },
    //   error: (error) => console.error('Error loading zones:', error)
    // });
    
    this.apiService.get('/severities').subscribe({
      next: (response: any) => {
        this.severities = response.data || [];
      },
      error: (error) => console.error('Error loading severities:', error)
    });
    
    this.apiService.get('/incident-types').subscribe({
      next: (response: any) => {
        this.incidentTypes = response.data || [];
        this.loadingFilters = false;
      },
      error: (error) => {
        console.error('Error loading incident types:', error);
        this.loadingFilters = false;
      }
    });
  }
  
  // onZoneChange(): void {
  //   if (this.selectedZoneId) {
  //     this.apiService.get(`/ward/zone/${this.selectedZoneId}`).subscribe({
  //       next: (response: any) => {
  //         this.wards = response.data || [];
  //       },
  //       error: (error) => console.error('Error loading wards:', error)
  //     });
  //   } else {
  //     this.wards = [];
  //     this.selectedWardId = null;
  //   }
  // }

  loadIncidents(): void {
  this.loading = true;
  this.error = null;

  const url = this.buildFilterUrl();

  this.apiService.get(url).subscribe({
    next: (response: any) => {

      this.incidents = response.data?.data || response.data || [];

      // Total records
      this.totalItems =
        response.meta?.total ??
        response.total ??
        0;

      // Total pages
      this.totalPages =
        response.meta?.last_page ??
        response.meta?.total_pages ??
        Math.ceil(this.totalItems / this.pageSize);

      this.loading = false;
    },

    error: (error) => {
      this.error = 'Failed to load incidents. Please try again.';
      this.loading = false;

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to load incidents. Please refresh the page.',
        confirmButtonColor: '#405189'
      });
    }
  });
}
  
  buildFilterUrl(): string {
    let params: string[] = [];
    
    params.push(`page=${this.currentPage}`);
    params.push(`limit=${this.pageSize}`);
    
    if (this.selectedFireStationId) {
      params.push(`fire_station_id=${this.selectedFireStationId}`);
    }
    
    if (this.selectedDate) {
      params.push(`start_date=${this.selectedDate}`);
    }
    
    if (this.selectedEndDate) {
      params.push(`end_date=${this.selectedEndDate}`);
    } else if (this.selectedDate) {
      params.push(`end_date=${this.selectedDate}`);
    }
    
    // if (this.selectedWardId) {
    //   params.push(`ward_id=${this.selectedWardId}`);
    // }
    
    // if (this.selectedZoneId) {
    //   params.push(`zone_id=${this.selectedZoneId}`);
    // }
    
    if (this.selectedStatus && this.selectedStatus !== 'all') {
      params.push(`status=${this.selectedStatus}`);
    }
    
    if (this.selectedSeverityId) {
      params.push(`severity_id=${this.selectedSeverityId}`);
    }
    
    
    return `/incidents?${params.join('&')}`;
  }

  getSeverityBadgeClass(incident: any): string {
    if (incident.severity?.color) {
      // Use custom color from API if available
      return ''; // Will use inline style
    }
    // Fallback to default colors
    switch (incident.severityId) {
      case 1: return 'bg-success';
      case 2: return 'bg-warning';
      case 3: return 'bg-danger';
      case 4: return 'bg-dark';
      default: return 'bg-secondary';
    }
  }

  getSeverityText(incident: any): string {
    return incident.severity?.name || this.getSeverityFallbackText(incident.severityId);
  }

  getSeverityFallbackText(severityId: number): string {
    switch (severityId) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Critical';
      default: return 'Unknown';
    }
  }

  getSeverityColor(incident: any): string {
    return incident.severity?.color || '';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true
});
  }

  refreshIncidents(): void {
    this.resetFilters();
    this.loadIncidents();
  }
  
  resetFilters(): void {
    this.selectedStatus = 'all';
    this.selectedDate = null;
    this.selectedEndDate = null;
    this.selectedFireStationId = null;
    this.selectedWardId = null;
    this.selectedZoneId = null;
    this.selectedSeverityId = null;
    this.selectedIncidentTypeId = null;
    this.currentPage = 1;
    this.wards = [];
  }
  
  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadIncidents();
    }
  }
  
  get pages(): number[] {
    const maxPages = 5;
    const pages: number[] = [];
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  ngOnDestroy(): void {
    // Unsubscribe from all socket subscriptions
    this.socketSubscriptions.forEach(sub => sub.unsubscribe());
    // Optionally disconnect socket if no other components need it
    // this.socketService.disconnect();
  }

onFilterChange(): void {
  // Filter change no longer auto-loads data
  // User must click Load button
}

applyFilters(): void {
  this.currentPage = 1;
  this.loadIncidents();
}

}
